import { useState } from 'react';
import { RiDeleteBin2Line, RiEyeLine, RiForbidLine, RiMore2Fill } from 'react-icons/ri';
import type { AiConnection } from '@/api/ai-connections';
import { ConfirmationModal } from '@/components/confirmation-modal';
import { Badge } from '@/components/primitives/badge';
import { Button } from '@/components/primitives/button';
import { CompactButton } from '@/components/primitives/button-compact';
import { Card, CardContent, CardHeader } from '@/components/primitives/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/primitives/dropdown-menu';
import { Skeleton } from '@/components/primitives/skeleton';
import { showErrorToast, showSuccessToast } from '@/components/primitives/sonner-helpers';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/primitives/table';
import { useAiConnectionActions } from '@/hooks/use-ai-connections';
import { formatDateSimple } from '@/utils/format-date';
import { ConnectionDetailSheet } from './connection-detail-sheet';
import { describeUserAgent } from './setup-instructions';

const STATUS_BADGE = {
  active: { color: 'green', label: 'Active' },
  revoked: { color: 'gray', label: 'Revoked' },
  expired: { color: 'orange', label: 'Expired' },
} as const;

type PendingAction = { type: 'revoke' | 'delete'; connection: AiConnection } | { type: 'revoke-all' };

type ConnectionsTableProps = {
  connections: AiConnection[] | undefined;
  isLoading: boolean;
  error: Error | null;
};

export function ConnectionsTable({ connections, isLoading, error }: ConnectionsTableProps) {
  const { revoke, remove, revokeAll } = useAiConnectionActions();
  const [pending, setPending] = useState<PendingAction>();
  const [detailId, setDetailId] = useState<string>();
  const activeCount = connections?.filter((c) => c.status === 'active').length ?? 0;

  const confirm = async () => {
    if (!pending) return;

    try {
      if (pending.type === 'revoke') {
        await revoke.mutateAsync(pending.connection.id);
        showSuccessToast(`${pending.connection.clientName} can no longer use your account.`);
      } else if (pending.type === 'delete') {
        await remove.mutateAsync(pending.connection.id);
        showSuccessToast(`${pending.connection.clientName} connection deleted.`);
      } else {
        const { revoked } = await revokeAll.mutateAsync();
        showSuccessToast(`Revoked ${revoked} connection${revoked === 1 ? '' : 's'}.`);
      }
      setPending(undefined);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Something went wrong.');
    }
  };

  return (
    <Card className="w-full overflow-hidden shadow-none">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          Your connections
          <p className="text-foreground-500 mt-1 text-xs font-normal">
            Every AI app you connected to your Novu account. Only you can see these.
          </p>
        </div>
        {activeCount > 1 && (
          <Button size="xs" variant="error" mode="outline" onClick={() => setPending({ type: 'revoke-all' })}>
            Revoke all
          </Button>
        )}
      </CardHeader>
      <CardContent className="rounded-b-xl border-t bg-white p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>App</TableHead>
              <TableHead>Connected</TableHead>
              <TableHead>Last used</TableHead>
              <TableHead>Where</TableHead>
              <TableHead>Calls / edits</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 2 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {!isLoading && error && (
              <TableRow>
                <TableCell colSpan={6} className="text-paragraph-xs text-text-soft py-6 text-center">
                  Couldn't load your connections: {error.message}
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !error && connections?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-paragraph-xs text-text-soft py-6 text-center">
                  No connections yet. Copy the setup prompt above to add one.
                </TableCell>
              </TableRow>
            )}
            {connections?.map((connection) => (
              <TableRow key={connection.id} className="group">
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="text-label-sm text-text-strong">{connection.clientName}</span>
                    <Badge color={STATUS_BADGE[connection.status].color} size="sm" variant="lighter">
                      {STATUS_BADGE[connection.status].label}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell className="text-paragraph-xs text-text-sub">
                  {formatDateSimple(connection.createdAt)}
                </TableCell>
                <TableCell className="text-paragraph-xs text-text-sub">
                  {connection.lastUsedAt ? formatDateSimple(connection.lastUsedAt) : 'Never'}
                </TableCell>
                <TableCell className="text-paragraph-xs text-text-sub">
                  <div className="flex flex-col">
                    <span>{connection.lastIp ?? connection.approvedIp ?? '—'}</span>
                    <span className="text-text-soft">{describeUserAgent(connection.lastUserAgent)}</span>
                  </div>
                </TableCell>
                <TableCell className="text-paragraph-xs text-text-sub">
                  {connection.callCount} / {connection.editCount}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <CompactButton icon={RiMore2Fill} variant="ghost" aria-label="Actions" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setDetailId(connection.id)}>
                        <RiEyeLine />
                        View activity
                      </DropdownMenuItem>
                      {connection.status === 'active' && (
                        <DropdownMenuItem onClick={() => setPending({ type: 'revoke', connection })}>
                          <RiForbidLine />
                          Revoke
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => setPending({ type: 'delete', connection })}
                      >
                        <RiDeleteBin2Line />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <ConfirmationModal
        open={Boolean(pending)}
        onOpenChange={(open) => !open && setPending(undefined)}
        onConfirm={confirm}
        isLoading={revoke.isPending || remove.isPending || revokeAll.isPending}
        confirmButtonVariant="error"
        title={
          pending?.type === 'revoke-all'
            ? 'Revoke all connections?'
            : pending?.type === 'delete'
              ? `Delete ${pending.connection.clientName}?`
              : `Revoke ${pending?.connection.clientName}?`
        }
        description={
          pending?.type === 'delete'
            ? 'The app loses access immediately and the connection is removed from this list. The record of edits it made stays.'
            : 'The app loses access immediately. You can connect it again any time.'
        }
        confirmButtonText={pending?.type === 'delete' ? 'Delete' : 'Revoke'}
      />
      <ConnectionDetailSheet connectionId={detailId} onClose={() => setDetailId(undefined)} />
    </Card>
  );
}
